import React, { useEffect, useMemo, useState } from 'react';
import { Control, FieldErrors, UseFormRegister, UseFormWatch, useWatch } from 'react-hook-form';

import SelectField from '@/components/atoms/SelectField';
import InputField from '@/components/atoms/InputField';
import AllocationBar from '@/components/organisms/AllocationBar';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { AccountingCenter } from '@/services/types/accounting-centers-types';
import { BankAccount } from '@/services/types/bank-accounts.types';
import { useBudgetPermissions } from '@/hooks/useBudgetPermissions';
import { getCurrencySymbol } from '@/utils/budgetCalculations';
import { formatRemainingAcohLabel, getAccountingCenterMemberAcoh } from '@/utils/workActivityForm';

interface CreateWorkAllocationProps {
  register: UseFormRegister<any>;
  errors: FieldErrors<any>;
  watch: UseFormWatch<any>;
  control: Control<any>;
  bankAccountsData: BankAccount[];
  accountingCentersData: AccountingCenter[];
  budgetId?: string;
  currency?: string;
}

interface AllocationOption {
  value: string;
  label: React.ReactNode;
  title: string;
  color: string;
}

const hexToHSL = (hex: string): { h: number; s: number; l: number } => {
  const r = parseInt(hex.substring(1, 3), 16) / 255;
  const g = parseInt(hex.substring(3, 5), 16) / 255;
  const b = parseInt(hex.substring(5, 7), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
    }
  }

  return { h: h * 360, s: s * 100, l: l * 100 };
};

const hslToHex = (h: number, s: number, l: number): string => {
  s /= 100;
  l /= 100;

  const k = (n: number) => (n + h / 30) % 12;
  const a = s * Math.min(l, 1 - l);
  const f = (n: number) => l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));

  const r = Math.round(255 * f(0));
  const g = Math.round(255 * f(8));
  const b = Math.round(255 * f(4));

  return `#${[r, g, b].map((x) => x.toString(16).padStart(2, '0')).join('')}`;
};

const generateColorForIndex = (index: number): string => {
  const baseColors = ['#7663EB', '#48C80C'];

  if (index < baseColors.length) {
    return baseColors[index];
  }

  const hsl1 = hexToHSL(baseColors[0]);
  const hsl2 = hexToHSL(baseColors[1]);

  const avgLightness = (hsl1.l + hsl2.l) / 2;
  const avgSaturation = (hsl1.s + hsl2.s) / 2;

  const randomHue = Math.floor(Math.random() * 360);
  const lightnessVariation = (Math.random() - 0.5) * 15;
  const saturationVariation = (Math.random() - 0.5) * 20;

  const newLightness = Math.max(30, Math.min(70, avgLightness + lightnessVariation));
  const newSaturation = Math.max(40, Math.min(100, avgSaturation + saturationVariation));

  return hslToHex(randomHue, newSaturation, newLightness);
};

const generateAccountingCenterOptions = (arr: AccountingCenter[], currentMemberId?: string): AllocationOption[] => {
  return arr.map((item, index) => {
    const { acoh, acohUsed } = getAccountingCenterMemberAcoh(item, currentMemberId);

    return {
      value: item._id,
      label: (
        <div>
          <p className="font-medium">{item?.name || '-'}</p>
          <p className="text-sm font-medium text-secondary">{formatRemainingAcohLabel(acoh, acohUsed)}</p>
        </div>
      ),
      title: item?.name || '-',
      color: generateColorForIndex(index)
    };
  });
};

const generateBankAccountOptions = (arr: BankAccount[], currency: string): AllocationOption[] => {
  const currencySymbol = getCurrencySymbol(currency);

  return arr.map((account, index) => ({
    value: account._id!,
    label: (
      <div>
        <p className="font-medium">{account?.accountName || '-'}</p>
        <p className="text-sm font-medium text-secondary">
          Remaining Balance {currencySymbol}
          {(account.remainingBalance ?? 0).toLocaleString()}
        </p>
      </div>
    ),
    title: account?.accountName || '-',
    color: generateColorForIndex(index)
  }));
};

const formatDuration = (totalMinutes: number) => {
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${hours}h ${minutes}m`;
};

const CreateWorkAllocation = ({
  register,
  errors,
  watch,
  control,
  bankAccountsData,
  accountingCentersData,
  budgetId,
  currency = 'EUR'
}: CreateWorkAllocationProps) => {
  const { member } = useBudgetPermissions({ budgetId });
  const currentMemberId = member?._id;

  const durationHours = Number(useWatch({ control, name: 'duration_hours' })) || 0;
  const durationMinutes = Number(useWatch({ control, name: 'duration_minutes' })) || 0;
  const totalDurationMinutes = durationHours * 60 + durationMinutes;

  const bankAccountIds: string[] = useWatch({ control, name: 'bank_account' }) || [];
  const departmentIds: string[] = useWatch({ control, name: 'department_accounting_center' }) || [];
  const productServicesIds: string[] = useWatch({ control, name: 'product_and_services_accounting_center' }) || [];
  const projectIds: string[] = useWatch({ control, name: 'project_accounting_center' }) || [];
  const contractIds: string[] = useWatch({ control, name: 'contract_accounting_center' }) || [];

  const allDynamicIds = useMemo(
    () => [...bankAccountIds, ...departmentIds, ...productServicesIds, ...projectIds, ...contractIds],
    [bankAccountIds, departmentIds, productServicesIds, projectIds, contractIds]
  );

  const allDynamicDurationFieldNames = useMemo(
    () => allDynamicIds.flatMap((id) => [`${id}_hours`, `${id}_minutes`]),
    [allDynamicIds]
  );
  const dynamicDurationValues: any[] = (useWatch({ control, name: allDynamicDurationFieldNames }) as any[]) || [];

  const durationByIdRef = useMemo(() => {
    const map: Record<string, number> = {};
    allDynamicIds.forEach((id, i) => {
      const hours = parseFloat(dynamicDurationValues[i * 2]) || 0;
      const minutes = parseFloat(dynamicDurationValues[i * 2 + 1]) || 0;
      map[id] = hours * 60 + minutes;
    });
    return map;
  }, [allDynamicIds, dynamicDurationValues]);

  const sumIds = (ids: string[]) => ids.reduce((sum, id) => sum + (durationByIdRef[id] || 0), 0);

  const isEqual = {
    bankAccounts: sumIds(bankAccountIds) === totalDurationMinutes,
    departmentsAC: sumIds(departmentIds) === totalDurationMinutes,
    productsAndServicesAC: sumIds(productServicesIds) === totalDurationMinutes,
    projectAC: sumIds(projectIds) === totalDurationMinutes,
    contractAC: sumIds(contractIds) === totalDurationMinutes
  };

  const [touchedFields, setTouchedFields] = useState<Set<string>>(new Set());
  const [touchedSections, setTouchedSections] = useState<Set<string>>(new Set());

  const handleFieldBlur = (fieldName: string, section: string) => {
    setTouchedFields((prev) => new Set(prev).add(fieldName));
    setTouchedSections((prev) => new Set(prev).add(section));
  };

  const [bankAccountsOptions, setBankAccountsOptions] = useState<AllocationOption[]>([]);
  const [accountingCentersOptions, setAccountingCentersOptions] = useState<{
    department: AllocationOption[];
    productAndServices: AllocationOption[];
    project: AllocationOption[];
    contract: AllocationOption[];
  }>({
    department: [],
    productAndServices: [],
    project: [],
    contract: []
  });

  useEffect(() => {
    setBankAccountsOptions(generateBankAccountOptions(bankAccountsData ?? [], currency));

    if (accountingCentersData) {
      setAccountingCentersOptions({
        department: generateAccountingCenterOptions(
          accountingCentersData.filter((ac) => ac.type === 'DEPARTMENT_ACCOUNTING'),
          currentMemberId
        ),
        productAndServices: generateAccountingCenterOptions(
          accountingCentersData.filter((ac) => ac.type === 'PRODUCTS_AND_SERVICES'),
          currentMemberId
        ),
        project: generateAccountingCenterOptions(
          accountingCentersData.filter((ac) => ac.type === 'PROJECTS'),
          currentMemberId
        ),
        contract: generateAccountingCenterOptions(
          accountingCentersData.filter((ac) => ac.type === 'CONTRACTS'),
          currentMemberId
        )
      });
    }
  }, [bankAccountsData, accountingCentersData, currency, currentMemberId]);

  const renderSection = (
    title: string,
    fieldName: string,
    options: AllocationOption[],
    sectionKey: keyof typeof isEqual
  ) => {
    const selectedIds: string[] = watch(fieldName) || [];

    return (
      <div className="h-fit card space-y-4">
        <p className="font-medium text-base">{title}</p>
        <SelectField
          name={fieldName}
          placeholder={`Select ${title.toLowerCase()}`}
          options={options}
          register={register}
          value={watch(fieldName)}
          error={errors[fieldName]}
          size="sm"
          multiple
          title={title.toLowerCase()}
        />

        {Array.isArray(selectedIds) && selectedIds.length > 0 && (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <p className="text-sm font-medium text-secondary">Duration Allocation</p>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button type="button" className="text-primary font-bold text-xs">
                      ?
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Split the total activity duration across the items selected above.</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <AllocationBar
              items={selectedIds.map((id) => {
                const optionData = options.find((option) => option.value === id);
                return {
                  value: durationByIdRef[id] || 0,
                  label: '',
                  color: optionData?.color || ''
                };
              })}
              maxAllowedTotal={totalDurationMinutes}
              height={22}
              borderRadius={12}
              isOverAllocated={touchedSections.has(sectionKey) && !isEqual[sectionKey]}
            />

            {selectedIds.map((id) => {
              const optionData = options.find((option) => option.value === id);
              return (
                <div key={id} className="flex flex-col gap-4 !mb-6 last:!mb-0">
                  <div className="flex-1 min-w-[120px] flex items-center gap-2">
                    <span className="w-[9px] h-[9px] rounded-full" style={{ background: optionData?.color }} />
                    <p className="flex-1 text-sm font-medium leading-[100%]">{optionData?.title || ''}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <InputField
                      name={`${id}_hours`}
                      placeholder="e.g 8"
                      register={register}
                      error={touchedFields.has(`${id}_hours`) ? errors[`${id}_hours`] : undefined}
                      onBlur={() => handleFieldBlur(`${id}_hours`, sectionKey)}
                      size="sm"
                      type="number"
                      className="flex-1"
                      unitDescription="Hours"
                      min={0}
                    />
                    <InputField
                      name={`${id}_minutes`}
                      placeholder="e.g 30"
                      register={register}
                      error={touchedFields.has(`${id}_minutes`) ? errors[`${id}_minutes`] : undefined}
                      onBlur={() => handleFieldBlur(`${id}_minutes`, sectionKey)}
                      size="sm"
                      type="number"
                      className="flex-1"
                      unitDescription="Min"
                      min={0}
                      max={59}
                    />
                  </div>
                </div>
              );
            })}

            {touchedSections.has(sectionKey) && !isEqual[sectionKey] && (
              <div className="text-red-500 text-sm">
                Allocated duration must equal the logged activity duration ({formatDuration(totalDurationMinutes)})
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="flex-1 grid grid-cols-1 lg:grid-cols-2 gap-4">
      {renderSection('Bank Account', 'bank_account', bankAccountsOptions, 'bankAccounts')}

      {accountingCentersOptions.department.length > 0 &&
        renderSection(
          'Department Accounting Center',
          'department_accounting_center',
          accountingCentersOptions.department,
          'departmentsAC'
        )}

      {accountingCentersOptions.productAndServices.length > 0 &&
        renderSection(
          'Product and Services Accounting Center',
          'product_and_services_accounting_center',
          accountingCentersOptions.productAndServices,
          'productsAndServicesAC'
        )}

      {accountingCentersOptions.project.length > 0 &&
        renderSection(
          'Projects Accounting Center',
          'project_accounting_center',
          accountingCentersOptions.project,
          'projectAC'
        )}

      {accountingCentersOptions.contract.length > 0 &&
        renderSection(
          'Contract Accounting Center',
          'contract_accounting_center',
          accountingCentersOptions.contract,
          'contractAC'
        )}
    </div>
  );
};

export default CreateWorkAllocation;
